import * as React from 'react';
import { Card, List, Typography, ListItemText } from '@mui/material';
import { InfoListItem, ChannelValue } from '@brightlayer-ui/react-components/core';
import { Data, isDataLoaded} from '../../types/data-providers';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AppRoute } from '../../routes';
import { DevicePropertiesDetails } from '../../types/device';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import _ from 'lodash';

type DeviceData = {
    deviceProperties: Data<DevicePropertiesDetails>;
};

const DeviceDetailRow: React.FC<{
    title: string;
    rightComponent?: JSX.Element;
    removeDivider?: boolean;
}> = (props) => (
    <div data-testid="device-detail-row">
        <InfoListItem
            title=""
            dense
            hidePadding
            rightComponent={              
              
                <div style={{textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap'}}>{props.rightComponent}</div>
               
                }
            
            leftComponent={
                <ListItemText>
                    <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        <Typography
                            variant="body2"
                            display="inline"
                        >
                            {props.title}
                        </Typography>
                    </div>
                </ListItemText>
            }
            divider={props.removeDivider ? undefined : 'full'}
        />
    </div>
);

export const DeviceProperties: React.FC<{ deviceData: DeviceData; appRoute: AppRoute }> = (props) => {
    const { t, formatDateTime } = useLanguageLocale();
    const realTimeSiteData = useSignalRSiteData();
    const [deviceStatus, setDeviceStatus] = React.useState<boolean | null >();

    React.useEffect(() => { 
        if (isDataLoaded(props.deviceData.deviceProperties)) {
             setDeviceStatus(props.deviceData.deviceProperties.value.status);
        }
    }, [props.deviceData.deviceProperties]);   

    React.useEffect(() => {
         if (realTimeSiteData && realTimeSiteData.devices) {
            if (isDataLoaded(props.deviceData.deviceProperties)) {
                 const updatedDeviceStatus =  _.find(realTimeSiteData.devices, {
                    deviceId: props.deviceData.deviceProperties.value.id
                });
                if(updatedDeviceStatus){
                    setDeviceStatus(updatedDeviceStatus.status);
                 }
            }   
        }  
    }, [realTimeSiteData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <Card>
                <List disablePadding>
                    <DataErrorIndicator
                        inline
                        message={t('DEVICE_DETAILS.DEVICE_CONNECTION_ERROR')}
                        data={props.deviceData.deviceProperties}
                    />

                    {isDataLoaded(props.deviceData.deviceProperties) && (
                        <div>
                            <DeviceDetailRow
                                title={t('DEVICE_DETAILS.STATUS')}
                                rightComponent={
                                    <ChannelValue
                                        value={
                                            deviceStatus
                                                ? t('DEVICE_DETAILS.ONLINE').toString()
                                                : t('DEVICE_DETAILS.OFFLINE').toString()
                                        }
                                    />
                                }
                            />


                            <DeviceDetailRow
                                title={t('DEVICE_DETAILS.NAME')}
                                rightComponent={
                                    <ChannelValue
                                        value={
                                            props.deviceData.deviceProperties.value.name ||
                                            t('DEVICE_DETAILS.EMPTY_VALUE')
                                        }
                                    />
                                }
                            />
                        {props.deviceData.deviceProperties.value.serialNumber &&  <DeviceDetailRow
                                title={t('DEVICE_DETAILS.DEVICE_SERIAL')}
                                rightComponent={
                                    <ChannelValue
                                        value={
                                            props.deviceData.deviceProperties.value.serialNumber
                                        }
                                    />
                                }
                            />}
                            <DeviceDetailRow
                                title={t('DEVICE_DETAILS.MAC')}
                                rightComponent={
                                    <ChannelValue
                                        value={ props.deviceData.deviceProperties.value.mac ||
                                         t('DEVICE_DETAILS.EMPTY_VALUE')}
                                    />
                                }
                            /> 
                            <DeviceDetailRow
                                title={t('DEVICE_DETAILS.ROLE')}
                                rightComponent={
                                    <ChannelValue
                                        value={ props.deviceData.deviceProperties.value.role ||
                                         t('DEVICE_DETAILS.EMPTY_VALUE')}
                                    />
                                }
                            /> 
                            <DeviceDetailRow
                                title={t('DEVICE_DETAILS.TYPE')}
                                rightComponent={
                                    <ChannelValue
                                        value={ props.deviceData.deviceProperties.value.family ||
                                         t('DEVICE_DETAILS.EMPTY_VALUE')}
                                    />
                                }
                            /> 
                            <DeviceDetailRow 
                                title={t('DEVICE_DETAILS.DEVICE_MODEL')}
                                rightComponent={
                                    <ChannelValue
                                        value={ props.deviceData.deviceProperties.value.type ||
                                         t('DEVICE_DETAILS.EMPTY_VALUE')}
                                    />
                                }
                            />     

                            <DeviceDetailRow 
                                title={t('DEVICE_DETAILS.VENDOR')}
                                rightComponent={
                                    <ChannelValue
                                        value={
                                            props.deviceData.deviceProperties.value.vendor ||
                                            t('DEVICE_DETAILS.EMPTY_VALUE')
                                        }
                                    />
                                }
                            /> 
                                <DeviceDetailRow
                                 removeDivider={true}
                                 title={props.deviceData.deviceProperties.value.isGatewaySyncTime ? t('DEVICE_DETAILS.CONNECTED_DEVICE_LAST_DATA') : t('DEVICE_DETAILS.LAST_DATA')}
                                rightComponent={
                                    <ChannelValue
                                        value={formatDateTime(props.deviceData.deviceProperties.value.syncedDate) ||
                                         t('DEVICE_DETAILS.EMPTY_VALUE')}
                                    />
                                }
                            /> 
                        </div>
                    )}
                </List>
            </Card>
        </div>
    );
};
